// /events — public listing of open events.
//
// Anonymous viewers see the AppShell-or-MarketingShell wrapped list; signed-in
// viewers also see their own interest state on each card.
//
// Wave 4M — date-range filter wired via TanStack Router search params, so
// links are bookmarkable. The bounds are passed through to /api/events as
// `from` / `to` (unix-ms ints) and applied to `events.starts_at` inclusively
// server-side.

import {
  Anchor,
  Badge,
  Card,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { MarketingShell } from '../../components/marketing/MarketingShell';
import { StateBadge } from '../../components/StateBadge';
import { PageFrame } from '../../components/ui/PageFrame';
import { useAuth } from '../../lib/auth-context';
import type { AnyState } from '../../lib/display-state';
import { track } from '../../lib/telemetry';

// STABLE, module-scope full-width AppShell wrapper — see events/$id.tsx. Inline
// `(props) => <AppShell tone="full">…` remounts the subtree every render, which
// dropped focus in the city filter and closed the date-range popover before an
// end date could be picked, for signed-in users (FR-028).
function AppShellFull({ children }: { children: React.ReactNode }) {
  return <AppShell tone="full">{children}</AppShell>;
}

type EventsSearch = {
  from?: number | undefined;
  to?: number | undefined;
};

export const Route = createFileRoute('/events/')({
  validateSearch: (search: Record<string, unknown>): EventsSearch => {
    const out: EventsSearch = {};
    if (typeof search.from === 'number' && Number.isFinite(search.from)) {
      out.from = search.from;
    } else if (typeof search.from === 'string' && search.from.length > 0) {
      const n = Number(search.from);
      if (Number.isFinite(n)) out.from = n;
    }
    if (typeof search.to === 'number' && Number.isFinite(search.to)) {
      out.to = search.to;
    } else if (typeof search.to === 'string' && search.to.length > 0) {
      const n = Number(search.to);
      if (Number.isFinite(n)) out.to = n;
    }
    return out;
  },
  component: EventsListPage,
});

type EventCardDTO = {
  id: string;
  title: string;
  description: string | null;
  artFormId: string | null;
  startsAt: number;
  endsAt: number;
  slotsTotal: number;
  slotsFilled: number;
  slotsRemaining: number;
  payCents: number | null;
  payNotes: string | null;
  state: AnyState['event'];
  publishedAt: number | null;
  createdAt: number;
  venue: {
    id: string;
    slug: string;
    name: string;
    city: string;
    country: string;
  };
  myInterest: { id: string; state: AnyState['event_interest'] } | null;
};

function EventsListPage() {
  const { user } = useAuth();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/events/' });

  const [items, setItems] = useState<EventCardDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [city, setCity] = useState<string>('');
  const [sort, setSort] = useState<'soonest' | 'latest_posted'>('soonest');

  // Date-range UI state — separate from URL search so the picker can show a
  // partially-filled range without committing it. Commits onto the URL via
  // navigate({ search: ... }) only when a value is picked.
  const fromDate = search.from !== undefined ? new Date(search.from) : null;
  const toDate = search.to !== undefined ? new Date(search.to) : null;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', '50');
        if (search.from !== undefined) params.set('from', String(search.from));
        if (search.to !== undefined) params.set('to', String(search.to));
        const res = await fetch(`/api/events?${params.toString()}`, {
          credentials: 'include',
        });
        const json = (await res.json()) as {
          ok: boolean;
          items?: EventCardDTO[];
          error?: { message?: string };
        };
        if (cancelled) return;
        if (!res.ok || !json.ok) {
          setError(json.error?.message ?? "Couldn't load events.");
          return;
        }
        setItems(json.items ?? []);
        setError(null);
      } catch {
        if (!cancelled) setError('Network problem loading events.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.from, search.to]);

  const visible = useMemo(() => {
    if (items === null) return null;
    let list = [...items];
    const q = city.trim().toLowerCase();
    if (q.length > 0) {
      list = list.filter((it) => it.venue.city.toLowerCase().includes(q));
    }
    list.sort((a, b) =>
      sort === 'soonest' ? a.startsAt - b.startsAt : b.createdAt - a.createdAt,
    );
    return list;
  }, [items, city, sort]);

  // Latest visible count via a ref so the effects below don't re-fire when
  // the result count alone changes (they should only fire on city/sort edits).
  const visibleCountRef = useRef<number>(0);
  visibleCountRef.current = visible?.length ?? 0;

  // Debounced city filter telemetry — fire after typing settles.
  const cityDebounceRef = useRef<number | null>(null);
  const initialCityRef = useRef<boolean>(true);
  useEffect(() => {
    if (initialCityRef.current) {
      initialCityRef.current = false;
      return;
    }
    if (cityDebounceRef.current !== null) window.clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = window.setTimeout(() => {
      cityDebounceRef.current = null;
      track('browse.filter_applied', {
        filterKey: 'city',
        resultCount: visibleCountRef.current,
      });
    }, 400);
    return () => {
      if (cityDebounceRef.current !== null) {
        window.clearTimeout(cityDebounceRef.current);
        cityDebounceRef.current = null;
      }
    };
  }, [city]);

  // Sort changes fire immediately — they don't cascade typing events.
  const initialSortRef = useRef<boolean>(true);
  useEffect(() => {
    if (initialSortRef.current) {
      initialSortRef.current = false;
      return;
    }
    track('browse.filter_applied', {
      filterKey: 'sort',
      resultCount: visibleCountRef.current,
    });
  }, [sort]);

  const onRangeChange = (range: [Date | string | null, Date | string | null]): void => {
    const [s, e] = range;
    const sDate = s instanceof Date ? s : s !== null ? new Date(s) : null;
    const eDate = e instanceof Date ? e : e !== null ? new Date(e) : null;
    track('browse.filter_applied', {
      filterKey: 'date_range',
      resultCount: visible?.length ?? 0,
    });
    void navigate({
      search: () => {
        const next: EventsSearch = {};
        if (sDate !== null && Number.isFinite(sDate.getTime())) {
          // Snap from-date to local-midnight so a "starts on this day" filter
          // is inclusive end-to-end.
          const startOfDay = new Date(sDate);
          startOfDay.setHours(0, 0, 0, 0);
          next.from = startOfDay.getTime();
        }
        if (eDate !== null && Number.isFinite(eDate.getTime())) {
          // Snap to-date to local end-of-day so events at any time on the
          // chosen day are still inside the filter.
          const endOfDay = new Date(eDate);
          endOfDay.setHours(23, 59, 59, 999);
          next.to = endOfDay.getTime();
        }
        return next;
      },
    });
  };

  // Dual-shell — see /events/$id for the same pattern. AppShell uses tone="full"
  // so PageFrame can own the inner layout identically across both shells.
  const Shell = user === null ? MarketingShell : AppShellFull;
  const inner = (
    <PageFrame tone="content">
      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={1}>Open events</Title>
          <Text c="dimmed">
            Hosts post the gigs they need filled. If something fits, send a note.
            The host decides who they want to talk to next.
          </Text>
        </Stack>

        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" wrap="wrap" align="flex-end">
            <TextInput
              label="Filter by city"
              placeholder="Brisbane"
              value={city}
              onChange={(e) => setCity(e.currentTarget.value)}
              w={{ base: '100%', sm: 240 }}
            />
            <DatePickerInput
              type="range"
              label="When"
              description="Show events starting in this window."
              placeholder="Pick a range"
              value={[fromDate, toDate]}
              onChange={onRangeChange}
              clearable
              w={{ base: '100%', sm: 280 }}
            />
            <Select
              label="Sort"
              value={sort}
              onChange={(v) => {
                if (v === 'soonest' || v === 'latest_posted') setSort(v);
              }}
              data={[
                { value: 'soonest', label: 'Soonest first' },
                { value: 'latest_posted', label: 'Recently posted' },
              ]}
              w={{ base: '100%', sm: 200 }}
            />
          </Group>
        </Card>

        {error !== null ? (
          <Text c="red">{error}</Text>
        ) : visible === null ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : visible.length === 0 ? (
          <EmptyState />
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {visible.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </PageFrame>
  );

  return <Shell>{inner}</Shell>;
}

function EventCard({ event }: { event: EventCardDTO }) {
  return (
    <Link
      to="/events/$id"
      params={{ id: event.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card withBorder padding="lg" radius="md" h="100%">
        <Stack gap="sm" h="100%">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={2}>
              <Text fw={600} lineClamp={2}>
                {event.title}
              </Text>
              <Text size="sm" c="dimmed">
                {event.venue.name} · {event.venue.city}
              </Text>
            </Stack>
            <StateBadge entity="event" state={event.state} />
          </Group>

          <Text size="sm" c="dimmed">
            {formatRange(event.startsAt, event.endsAt)}
          </Text>

          {event.payCents !== null || event.payNotes !== null ? (
            <Text size="sm">{formatPay(event.payCents, event.payNotes)}</Text>
          ) : null}

          <Group gap="xs" mt="auto">
            <Badge variant="light" color="gray">
              {event.slotsRemaining} of {event.slotsTotal} open
            </Badge>
            {event.myInterest !== null ? (
              <StateBadge entity="event_interest" state={event.myInterest.state} size="sm" />
            ) : null}
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}

function EmptyState() {
  return (
    <Card withBorder radius="md" p="xl">
      <Stack align="center" gap="sm">
        <Text c="dimmed" ta="center">
          Nothing open right now. Check back soon. The road's quiet, not finished.
        </Text>
        <Anchor component={Link} to="/browse">
          Browse venues
        </Anchor>
      </Stack>
    </Card>
  );
}

function formatRange(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
  }
  return `${dateFmt.format(start)} → ${dateFmt.format(end)}`;
}

function formatPay(cents: number | null, notes: string | null): string {
  if (cents !== null && cents > 0) {
    const dollars = (cents / 100).toFixed(0);
    return notes !== null ? `$${dollars} AUD · ${notes}` : `$${dollars} AUD`;
  }
  return notes ?? '';
}
